import React from "react";
import { Box } from "@mui/material";
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";

import { promptService } from "../../services/PromptService";

import FilterContainer from "./FilterContainer";
const findInitialValues = (filters) => {
  let initialValues = {};

  Object.values(filters || {}).forEach((filter) => {
    initialValues[filter.label] = filter.value;
  });

  return initialValues;
};

export const FilterPanel = forwardRef(function FilterPanel(props, ref) {
  const { filters, config, handleFilterChange, currentDash } = props;
  const [values, setValues] = useState(findInitialValues(filters));

  // Expose the current selection so the parent can answer prompts with the
  // values the user actually sees, even if the global filter state was
  // overwritten by a background render/adapt cycle.
  useImperativeHandle(ref, () => ({
    getValues: () => values,
  }));
  const dashboardFilters = promptService.getDisplayedFiltersForDashboard(
    filters,
    currentDash,
    config,
  );

  useEffect(() => {
    const nextDashboardFilters = promptService.getDisplayedFiltersForDashboard(
      filters,
      currentDash,
      config,
    );

    // Preserve the user's current selection across tab switches: only adopt
    // the dossier's own value for filters that didn't exist before (first
    // load or a filter that the previous tab didn't have).
    setValues((prev) => {
      const next = findInitialValues(nextDashboardFilters);

      Object.keys(next).forEach((label) => {
        if (prev[label] !== undefined) {
          next[label] = prev[label];
        }
      });

      return next;
    });
  }, [filters, currentDash, config]);

  const preferredOrder = ["Year", "Month", "Region", "Country"];
  const orderedFilters = Object.values(dashboardFilters)
    .filter((filter) => filter.label !== "Meeting")
    .sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.label);
      const bIndex = preferredOrder.indexOf(b.label);
      const safeA = aIndex === -1 ? preferredOrder.length + 1 : aIndex;
      const safeB = bIndex === -1 ? preferredOrder.length + 1 : bIndex;

      return safeA - safeB;
    });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: "8px",
        minWidth: 0,
        width: "100%",
        marginLeft: "auto",
        overflow: "hidden",
      }}
    >
      {orderedFilters.map((filter, index) => (
        <FilterContainer
          key={filter.label || index}
          config={config}
          currentDash={currentDash}
          elements={
            filter.displayedElements == undefined ? filter.elements : filter.displayedElements
          }
          filter={filter}
          filters={dashboardFilters}
          handleFilterChange={handleFilterChange}
          index={index}
          initialValue={values[filter.label]}
          label={filter.label}
          setValues={setValues}
          values={values}
        />
      ))}
    </Box>
  );
});
